/* eslint-disable @typescript-eslint/no-explicit-any */
import { getBgpVisibilityData, getBgpVisibilityRoutes } from '../../Reflector/request/request';
import { CPR, SelectedEntity, CprEntry, CloudResourceTag, TagAttribution, VisibilityMemberResponse, EntityStatusInfo, normalizeRouteFamily, isUnicastFamily } from '../BgpVisibilityData';
import { mapWireRouteRows, RouteRow, WireRouteRow } from '../utils/routeModel';

/**
 * Reads a wire tag (`targets[].route_table_tag` / `subnet_tag`) into `CloudResourceTag`.
 *
 * Returns `null` for anything that isn't a usable `{ name, value }` pair — absent field, wrong
 * shape, or a blank name — so a malformed or partial tag can never surface as an empty chip
 * claiming the destination was tag-discovered (CTX-129). A blank *value* is preserved: an AWS
 * tag may legitimately have an empty value.
 */
export function normalizeCloudResourceTag(raw: any): CloudResourceTag | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const name = typeof raw.name === 'string' ? raw.name.trim() : '';
  if (!name) {
    return null;
  }
  return { name, value: typeof raw.value === 'string' ? raw.value : String(raw.value ?? '') };
}

/**
 * Normalizes the two wire shapes that carry tag provenance into one `TagAttribution`:
 *
 *   OUTBOUND — `tag_attribution: { source, tags: [...] }` on the prefix record
 *   INBOUND  — `targets[].route_table_tag: { name, value }`, a single tag with no source
 *
 * The directions genuinely disagree on the wire, so both are read and whichever is present
 * wins; `tag_attribution` takes precedence since it is the richer of the two.
 *
 * Returns null when nothing usable is present — including an attribution whose tags all fail
 * `normalizeCloudResourceTag`, so a malformed payload can never render as a tagged destination.
 * An attribution carrying only a `source` (e.g. `manual`) is kept, since that IS information.
 */
export function normalizeTagAttribution(rawAttribution: any, rawSingleTag?: any): TagAttribution | null {
  const single = normalizeCloudResourceTag(rawSingleTag);

  if (rawAttribution && typeof rawAttribution === 'object') {
    const rawTags = Array.isArray(rawAttribution.tags) ? rawAttribution.tags : [];
    const tags = rawTags
      .map(normalizeCloudResourceTag)
      .filter((tag: CloudResourceTag | null): tag is CloudResourceTag => tag !== null);
    const source = typeof rawAttribution.source === 'string' && rawAttribution.source.trim()
      ? rawAttribution.source.trim()
      : null;

    if (tags.length > 0 || source) {
      return { source, tags };
    }
  }

  return single ? { source: null, tags: [single] } : null;
}

/**
 * How a caller identifies a cluster member. The two data sources disagree on the member key:
 * the sidebar (`getClusterReflectors`) always exposes `{ id: subId, display_name: name }`,
 * while the `/visibility` response's `member.id` is the reflector **subId** on the CTX-25
 * backend but the instance **name** on older deployments (still live on staging). Carrying
 * BOTH the subId and the name here lets `memberMatchesSelection` bridge those id-spaces so
 * member selection resolves correctly regardless of which shape the endpoint serves.
 */
export interface ClusterMemberSelection {
  id?: string | null;
  name?: string | null;
}

function selectionKeys(selection: string | ClusterMemberSelection | null | undefined): string[] {
  if (selection == null) {
    return [];
  }
  if (typeof selection === 'string') {
    return selection ? [selection] : [];
  }
  return [selection.id, selection.name].filter((v): v is string => !!v);
}

/**
 * True when a `/visibility` member matches the caller's selection by EITHER its id or its name.
 * This is the id-space bridge: it matches whether the response keys the member by subId (new
 * backend) or by name (old backend), and whether the caller identifies it by subId (sidebar) or
 * by name. A bare string is treated as a single candidate key.
 */
export function memberMatchesSelection(
  member: { id: string; name?: string | null },
  selection: string | ClusterMemberSelection | null | undefined
): boolean {
  const keys = selectionKeys(selection);
  if (keys.length === 0) {
    return false;
  }
  return keys.includes(member.id) || (member.name != null && keys.includes(member.name));
}

/**
 * Normalizes the HA role from the `/visibility` payload to the canonical UPPERCASE form the UI
 * compares against. The deployed backend returns it lowercase (`"primary"` / `"secondary"`),
 * while the badge and the PRIMARY-down fallback match the uppercase literals — so raw values
 * must be normalized at ingestion. Anything outside the known set (including null/undefined)
 * collapses to null, i.e. "no role".
 */
export function normalizeClusterRole(raw: unknown): 'PRIMARY' | 'SECONDARY' | null {
  if (typeof raw !== 'string') {
    return null;
  }
  const upper = raw.toUpperCase();
  return upper === 'PRIMARY' || upper === 'SECONDARY' ? upper : null;
}

/**
 * The single place a cluster HA role becomes display text ("Primary" / "Secondary"), for the
 * selector pill, the table's Reflector chips and the down-overlay copy alike.
 *
 * Deliberately takes `unknown` and normalizes internally, so it is case-insensitive at the point
 * of use: the backend returns the role lowercase, and any path that reaches a view without
 * passing through `normalizeClusterRole` at ingestion would otherwise silently render no role at
 * all. Returns null when there is no recognizable role, so callers can omit the label entirely.
 */
export function formatClusterRole(raw: unknown): 'Primary' | 'Secondary' | null {
  const normalized = normalizeClusterRole(raw);
  if (!normalized) {
    return null;
  }
  return normalized === 'PRIMARY' ? 'Primary' : 'Secondary';
}

/**
 * Gets the raw cluster API response (array of all reflectors' data)
 * @param token - Authentication token
 * @param clusterId - Cluster ID
 * @param direction - Direction: 'inbound' or 'outbound'
 * @param tenantId - Tenant ID
 * @param signal - Optional abort signal; a superseded selection cancels its own request (CTX-276)
 * @returns Raw array response or null if error
 */
export async function getRawClusterResponse(
  token: string,
  clusterId: string,
  direction: 'inbound' | 'outbound',
  tenantId: number,
  signal?: AbortSignal
): Promise<VisibilityMemberResponse[] | null> {
  try {
    const response = await getBgpVisibilityData(token, 'cluster', clusterId, direction, tenantId, signal);

    // Response structure (CTX-25 contract): [{ id: subId, name, status, lastUpdate, data | null }]
    // A 404 here means NO member has a document at all; individual down members still arrive
    // with data: null and must not be treated as absent.
    if (!response.data || !Array.isArray(response.data) || response.data.length === 0) {
      return null;
    }

    return response.data;
  } catch (error) {
    // An aborted request is a superseded selection, not a failure — don't report it as one.
    if (!signal?.aborted) {
      console.error('[BGP Visibility API] Error fetching raw cluster response:', error);
    }
    return null;
  }
}

/**
 * Finds the status/availability info for a specific member (reflector or cluster member)
 * from a `/v2/reflector/visibility` response, regardless of whether it has data. Members are
 * matched through the id-space bridge (`memberMatchesSelection`) so a selection expressed as a
 * subId still resolves when the response keys members by name (older backend), and vice versa.
 */
export function getEntityStatusInfo(
  responseData: VisibilityMemberResponse[] | null,
  selection: string | ClusterMemberSelection | null
): EntityStatusInfo | null {
  if (!responseData || responseData.length === 0) {
    return null;
  }

  const item = selectionKeys(selection).length > 0
    ? responseData.find(member => memberMatchesSelection(member, selection))
    : responseData[0];

  if (!item) {
    return null;
  }

  return {
    id: item.id,
    name: item.name,
    role: normalizeClusterRole(item.role),
    status: item.status,
    lastUpdate: item.lastUpdate,
    hasData: item.data !== null && item.data !== undefined
  };
}

export interface FetchCprResult {
  cpr: CPR | null;
  statusInfo: EntityStatusInfo | null;
}

/**
 * Fetches real CPR data from the API for a selected entity
 * @param token - Authentication token
 * @param selectedEntity - The selected entity (reflector or cluster)
 * @param direction - Direction: 'inbound' or 'outbound'
 * @param tenantId - Tenant ID
 * @param selectedClusterReflector - Optional: selected reflector ID/name when entity is a cluster
 * @param signal - Optional abort signal; a superseded selection cancels its own request (CTX-276)
 * @returns CPR data plus the member's availability status. `cpr` is null both when the member
 * has no S3 document yet (down/no data — check `statusInfo.hasData`) and on outright errors.
 */
export async function fetchCprDataFromApi(
  token: string,
  selectedEntity: SelectedEntity | null,
  direction: 'inbound' | 'outbound',
  tenantId: number,
  selectedClusterReflector?: string | null,
  signal?: AbortSignal
): Promise<FetchCprResult> {
  if (!selectedEntity) {
    return { cpr: null, statusInfo: null };
  }

  try {
    // Use the entity type and name/ID directly from selectedEntity
    // For reflectors (standalone mode): subId equals id, so we use subId
    // For clusters: we use subId (not id)
    const entityType = selectedEntity.type;

    // For both reflectors and clusters, use subId (fallback to id if subId not available)
    const nameOrId = selectedEntity.subId || selectedEntity.id;

    // Normalize direction to lowercase for API
    const apiDirection = direction.toLowerCase() as 'inbound' | 'outbound';

    // Make API call
    const response = await getBgpVisibilityData(token, entityType, nameOrId, apiDirection, tenantId, signal);

    // Response structure (CTX-25 contract): [{ id: subId, name, role, status, lastUpdate, data | null }]
    if (!response.data || !Array.isArray(response.data) || response.data.length === 0) {
      return { cpr: null, statusInfo: null };
    }

    const members: VisibilityMemberResponse[] = response.data;

    // Match strictly by subId (`id`) — never by `name`, which is display-only and not unique.
    // No fallback-to-first: presenting another member's document under the selected member's
    // identity would show stale/wrong data as if it were live.
    const responseItem = entityType === 'cluster' && selectedClusterReflector
      ? members.find(item => item.id === selectedClusterReflector)
      : members[0];

    if (!responseItem) {
      return { cpr: null, statusInfo: null };
    }

    const statusInfo: EntityStatusInfo = {
      id: responseItem.id,
      name: responseItem.name,
      role: responseItem.role,
      status: responseItem.status,
      lastUpdate: responseItem.lastUpdate,
      hasData: responseItem.data !== null && responseItem.data !== undefined
    };

    if (!responseItem.data) {
      // Reflector down / no data yet — not an error, caller renders the down state via statusInfo
      return { cpr: null, statusInfo };
    }

    // Extract the data - data contains neighbors/vpcs directly, wrap in INBOUND/OUTBOUND
    let apiData;
    if (responseItem.data.neighbors || responseItem.data.vpcs) {
      // Current structure: data contains neighbors/vpcs directly, wrap in INBOUND/OUTBOUND
      apiData = {
        INBOUND: responseItem.data,
        OUTBOUND: responseItem.data.OUTBOUND || null
      };
    } else if (responseItem.data.INBOUND || responseItem.data.OUTBOUND) {
      // Alternative: data already has INBOUND/OUTBOUND structure
      apiData = responseItem.data;
    } else {
      return { cpr: null, statusInfo };
    }

    // Transform API response to CPR structure
    const cprData = transformApiResponseToCPR(apiData, responseItem.id, entityType);

    return { cpr: cprData, statusInfo };
  } catch (error) {
    // An aborted request is a superseded selection, not a failure — don't report it as one.
    if (!signal?.aborted) {
      console.error('[BGP Visibility API] Error fetching CPR data:', error);
    }
    return { cpr: null, statusInfo: null };
  }
}

/**
 * Transforms OUTBOUND data to CprEntry format (handles both new neighbors structure and legacy vpcs structure)
 * @param outboundData - The OUTBOUND data from API response
 * @returns Array of CprEntry objects
 */
function transformOutboundDataToCprEntries(outboundData: any): CprEntry[] {
  if (!outboundData) {
    return [];
  }

  // Check for new neighbors structure first
  if (outboundData.neighbors && Array.isArray(outboundData.neighbors)) {
    return transformOutboundNeighborsToCprEntries(outboundData);
  }

  // Fallback to legacy vpcs structure
  if (outboundData.vpcs && Array.isArray(outboundData.vpcs)) {
    return transformOutboundVpcsToCprEntries(outboundData);
  }

  return [];
}

/**
 * Transforms new OUTBOUND structure (with neighbors array) to CprEntry format
 * @param outboundData - The OUTBOUND data from API response with neighbors structure
 * @returns Array of CprEntry objects
 */
function transformOutboundNeighborsToCprEntries(outboundData: any): CprEntry[] {
  if (!outboundData || !outboundData.neighbors || !Array.isArray(outboundData.neighbors)) {
    return [];
  }

  const entries: CprEntry[] = [];

  // Iterate through neighbors
  for (const neighbor of outboundData.neighbors) {
    if (!neighbor.neighbor_ip || !neighbor.prefixes || !Array.isArray(neighbor.prefixes)) {
      continue;
    }

    // Iterate through prefixes for this neighbor
    for (const prefixData of neighbor.prefixes) {
      if (!prefixData.cidr || !prefixData.targets || !Array.isArray(prefixData.targets)) {
        continue;
      }

      const family = normalizeRouteFamily(prefixData.family);

      // A prefix is withdrawn iff its action is 'delete' — same rule as inbound, no other checks.
      const isWithdrawn = prefixData.action === 'delete' || prefixData.decision_reason === 'delete_explicit_withdraw';

      // For outbound, determine if this is the best path
      // Check if there's a candidate with this neighbor_ip and selected: true
      // If no candidates exist, assume it's the best (only) path
      let isBestPath = false;
      if (prefixData.candidates && Array.isArray(prefixData.candidates) && prefixData.candidates.length > 0) {
        // Pink line condition: If candidate's neighbor_ip matches parent neighbor_ip AND selected is true
        const selectedCandidate = prefixData.candidates.find(
          (c: any) => c.neighbor_ip === neighbor.neighbor_ip && c.selected === true
        );
        isBestPath = !!selectedCandidate;
      } else {
        // No candidates means this is likely the only path, so it's the best
        isBestPath = true;
      }

      // Build candidates array (if they exist in outbound data)
      const candidates: any[] = [];
      if (prefixData.candidates && Array.isArray(prefixData.candidates)) {
        prefixData.candidates.forEach((candidate: any) => {
          // Skip the candidate that matches the main neighbor (already represented in main entry)
          if (candidate.neighbor_ip !== neighbor.neighbor_ip) {
            candidates.push({
              neighbor_ip: candidate.neighbor_ip,
              local_pref: candidate.local_pref || 100,
              med: candidate.med || 0,
              as_path: Array.isArray(candidate.as_path) ? candidate.as_path : [],
              origin: candidate.origin || 'INCOMPLETE',
              selected: candidate.selected === true
            });
          }
        });
      }

      // Create one entry per target (vpc_id + subnet_id)
      for (const target of prefixData.targets) {
        if (!target.vpc_id || !target.subnet_id) {
          continue;
        }

        const entry: CprEntry = {
          prefix: prefixData.cidr,
          vpc: target.vpc_id,
          family: family,
          route_table_id: target.subnet_id, // Use subnet_id as route_table_id for outbound
          action: isWithdrawn ? 'deny' : 'permit',
          neighbor_ip: neighbor.neighbor_ip,
          next_hop_type: 'IP', // Outbound uses IP next-hop
          specific_next_hop: prefixData.next_hop || null,
          local_pref: prefixData.local_pref || 100,
          med: 0, // MED not typically used in outbound
          as_path: [], // AS_PATH not in outbound announcements
          origin: prefixData.origin || 'INCOMPLETE',
          rd: null,
          rt: null,
          candidates: candidates,
          decision_reason: `Outbound announcement to ${neighbor.neighbor_ip}`,
          timestamp: outboundData.timestamp || Date.now(),
          __source: outboundData.__source || '',
          // Add selected field to indicate if this is a best path
          selected: isBestPath,
          // Outbound-specific fields
          subnet_id: target.subnet_id,
          rule_id: prefixData.rule_id || null,
          status: prefixData.status,
          extensions: prefixData.extensions || null,
          withdrawn: isWithdrawn,
          withdrawn_neighbor_ip: target.withdrawn_neighbor_ip || prefixData.withdrawn_neighbor_ip || null,
          received_at: target.received_at || prefixData.received_at || null,
          // Outbound carries `tag_attribution` on the prefix record (confirmed against a live
          // capture), so it applies to every target of that prefix.
          tag_attribution: normalizeTagAttribution(prefixData.tag_attribution, target.subnet_tag)
        };

        entries.push(entry);
      }
    }
  }

  return entries;
}

/**
 * Transforms new INBOUND structure (with neighbors array) to CprEntry format
 * @param inboundData - The INBOUND data from API response with neighbors structure
 * @returns Array of CprEntry objects
 */
function transformInboundNeighborsToCprEntries(inboundData: any): CprEntry[] {
  if (!inboundData) {
    return [];
  }

  const entries: CprEntry[] = [];

  // A prefix record is a withdraw tombstone under either wire encoding: the older
  // action='delete', or the explicit-peer-withdraw decision_reason='delete_explicit_withdraw'.
  const isWithdrawnRecord = (rec: any): boolean =>
    rec?.action === 'delete' || rec?.decision_reason === 'delete_explicit_withdraw';

  /**
   * Emits CprEntry rows for one prefix record. Tolerates two wire shapes:
   *  - nested announcements: targets[] carry vpc_id + route_table_id, neighbor_ip on the parent
   *  - flat withdraw tombstones: route_table_id/vpc at the top level, no targets[], neighbor_ip
   *    null, and the withdrawing peer in withdrawn_neighbor_ip
   */
  const pushPrefixEntries = (prefixData: any, parentNeighborIp: string | null) => {
    if (!prefixData || !prefixData.prefix) {
      return;
    }

    const family = normalizeRouteFamily(prefixData.family);
    const isWithdrawn = isWithdrawnRecord(prefixData);

    // Best path: a candidate for the parent neighbor marked selected. No candidates → sole path.
    let isBestPath = false;
    if (Array.isArray(prefixData.candidates) && prefixData.candidates.length > 0) {
      const selectedCandidate = prefixData.candidates.find(
        (c: any) => c.neighbor_ip === parentNeighborIp && c.selected === true
      );
      isBestPath = !!selectedCandidate;
    } else {
      isBestPath = true;
    }

    // Alternate-path candidates (exclude the one already represented by the main entry).
    const candidates: any[] = [];
    if (Array.isArray(prefixData.candidates)) {
      prefixData.candidates.forEach((candidate: any) => {
        if (candidate.neighbor_ip && candidate.neighbor_ip !== parentNeighborIp) {
          candidates.push({
            neighbor_ip: candidate.neighbor_ip,
            local_pref: candidate.local_pref || 100,
            med: candidate.med || 0,
            as_path: Array.isArray(candidate.as_path) ? candidate.as_path : [],
            origin: candidate.origin || 'INCOMPLETE',
            selected: candidate.selected === true
          });
        }
      });
    }

    // Neighbor identity: real neighbor for announcements; for withdraw tombstones the wire
    // neighbor_ip is null, so fall back to withdrawn_neighbor_ip (the peer that withdrew it).
    const resolvedNeighborIp =
      prefixData.neighbor_ip ||
      parentNeighborIp ||
      (isWithdrawn ? prefixData.withdrawn_neighbor_ip : null) ||
      '';

    // Targets: nested targets[] when present, else synthesize one from the record's top-level
    // route_table_id/vpc (the flat withdraw-tombstone shape).
    const targets =
      Array.isArray(prefixData.targets) && prefixData.targets.length > 0
        ? prefixData.targets
        : prefixData.route_table_id
          ? [
              {
                vpc_id: prefixData.vpc ?? prefixData.vpc_id ?? null,
                route_table_id: prefixData.route_table_id,
                withdrawn_neighbor_ip: prefixData.withdrawn_neighbor_ip,
                received_at: prefixData.received_at
              }
            ]
          : [];

    for (const target of targets) {
      // Announcements still require a route table + vpc; withdraws only need a route table.
      if (!target.route_table_id || (!isWithdrawn && !target.vpc_id)) {
        continue;
      }

      entries.push({
        prefix: prefixData.prefix,
        vpc: target.vpc_id || 'withdrawn',
        family,
        route_table_id: target.route_table_id,
        action: isWithdrawn ? 'deny' : 'permit',
        neighbor_ip: resolvedNeighborIp,
        next_hop_type: prefixData.next_hop_type || 'IP',
        specific_next_hop: prefixData.specific_next_hop || null,
        local_pref: prefixData.local_pref || 100,
        med: prefixData.med !== null && prefixData.med !== undefined ? prefixData.med : 0,
        as_path: Array.isArray(prefixData.as_path) ? prefixData.as_path : [],
        origin: prefixData.origin || 'INCOMPLETE',
        rd: prefixData.rd || null,
        rt: prefixData.rt || null,
        candidates,
        decision_reason: prefixData.decision_reason || '',
        timestamp: prefixData.timestamp || Date.now(),
        __source: inboundData.__source || '',
        selected: isBestPath,
        withdrawn: isWithdrawn,
        withdrawn_neighbor_ip: target.withdrawn_neighbor_ip || prefixData.withdrawn_neighbor_ip || null,
        received_at: target.received_at || prefixData.received_at || null,
        tag_attribution: normalizeTagAttribution(prefixData.tag_attribution, target.route_table_tag),
        // Cloud outcome per (prefix, route table), CTX-323. The target carries the
        // exact value for THIS route table; the prefix-level copy is the fallback for
        // wire shapes without targets[] (withdraw tombstones). Left undefined when the
        // reflector reports neither, which `deriveCloudRouteHealth` reads as "no data".
        injection_status: target.injection_status ?? prefixData.injection_status,
        injection_error: target.injection_error ?? prefixData.injection_error,
        injection_at: target.injection_at ?? prefixData.injection_at,
        aws_state: target.aws_state ?? prefixData.aws_state,
        aws_checked_at: target.aws_checked_at ?? prefixData.aws_checked_at
      } as CprEntry);
    }
  };

  // 1) Prefixes grouped under neighbors (announcements + any nested withdraws).
  if (Array.isArray(inboundData.neighbors)) {
    for (const neighbor of inboundData.neighbors) {
      if (!neighbor || !Array.isArray(neighbor.prefixes)) {
        continue;
      }
      for (const prefixData of neighbor.prefixes) {
        pushPrefixEntries(prefixData, neighbor.neighbor_ip ?? null);
      }
    }
  }

  // 2) Flat withdraw tombstones delivered outside the neighbors tree (real backend shape:
  //    neighbor_ip null, route_table_id at the top level). Accept the common container names.
  for (const arr of [inboundData.withdrawn, inboundData.withdrawals, inboundData.deleted]) {
    if (Array.isArray(arr)) {
      for (const rec of arr) {
        pushPrefixEntries(rec, rec?.neighbor_ip ?? null);
      }
    }
  }

  return entries;
}

/**
 * Transforms legacy OUTBOUND structure (with vpcs array) to CprEntry format
 * @param outboundData - The OUTBOUND data from API response with vpcs structure
 * @returns Array of CprEntry objects
 */
function transformOutboundVpcsToCprEntries(outboundData: any): CprEntry[] {
  if (!outboundData || !outboundData.vpcs || !Array.isArray(outboundData.vpcs)) {
    return [];
  }

  const entries: CprEntry[] = [];

  // Iterate through VPCs
  for (const vpc of outboundData.vpcs) {
    if (!vpc.subnets || !Array.isArray(vpc.subnets)) {
      continue;
    }

    // Iterate through subnets
    for (const subnet of vpc.subnets) {
      if (!subnet.announcements || !Array.isArray(subnet.announcements)) {
        continue;
      }

      // Iterate through announcements
      for (const announcement of subnet.announcements) {
        const entry: CprEntry = {
          prefix: subnet.cidr,
          vpc: vpc.vpc_id,
          family: normalizeRouteFamily(announcement.family),
          route_table_id: subnet.subnet_id, // Use subnet_id as route_table_id for outbound
          action: 'permit', // Outbound is always permit
          neighbor_ip: announcement.neighbor_ip,
          next_hop_type: 'IP', // Outbound uses IP next-hop
          specific_next_hop: announcement.next_hop,
          local_pref: announcement.local_pref || 100,
          med: 0, // MED not typically used in outbound
          as_path: [], // AS_PATH not in outbound announcements
          origin: announcement.origin || 'INCOMPLETE',
          rd: null,
          rt: null,
          candidates: [], // No candidates in legacy outbound
          decision_reason: `Outbound announcement to ${announcement.neighbor_ip}`,
          timestamp: outboundData.timestamp || Date.now(),
          __source: outboundData.__source || '',
          // Outbound-specific fields
          subnet_id: subnet.subnet_id,
          rule_id: subnet.rule_id,
          status: announcement.status,
          extensions: announcement.extensions,
          withdrawn_neighbor_ip: null,
          received_at: null
        };

        entries.push(entry);
      }
    }
  }

  return entries;
}

/**
 * Transforms API response data to CPR structure
 * @param apiData - The data field from API response with INBOUND/OUTBOUND structure
 * @param entityId - Entity ID from response
 * @param entityType - Entity type (reflector or cluster)
 * @returns CPR structure
 */
function transformApiResponseToCPR(
  apiData: any,
  entityId: string,
  entityType: 'reflector' | 'cluster'
): CPR | null {
  if (!apiData) {
    return null;
  }

  // Handle inbound data - transform from neighbors array structure
  let inboundIpv4Entries: CprEntry[] = [];
  let inboundVpnv4Entries: CprEntry[] = [];
  
  if (apiData.INBOUND) {
    // Transform from the neighbors array structure and/or any flat withdraw tombstone list.
    // The transform tolerates a missing `neighbors` array (flat-only withdraw payloads).
    const allEntries = transformInboundNeighborsToCprEntries(apiData.INBOUND);
    // Separate by family. IPv6 unicast shares the unicast bucket, as it does on the wire.
    inboundIpv4Entries = allEntries.filter(e => isUnicastFamily(e.family));
    inboundVpnv4Entries = allEntries.filter(e => e.family === 'vpnv4');
  }
  
  const inboundData = {
    ipv4_unicast_cpr: inboundIpv4Entries,
    vpnv4_cpr: inboundVpnv4Entries
  };

  // Handle outbound data - transform from neighbors array structure (new) or vpcs array structure (legacy)
  // The transformOutboundDataToCprEntries function handles both structures automatically
  let outboundEntries: CprEntry[] = [];
  let rawOutboundData = null;

  if (apiData.OUTBOUND) {
    rawOutboundData = apiData.OUTBOUND;
    outboundEntries = transformOutboundDataToCprEntries(apiData.OUTBOUND);
  }

  const cpr: CPR = {
    entity: {
      id: entityId,
      type: entityType
    },
    filters: {
      prefix: apiData.filters?.prefix || null,
      family: apiData.filters?.family || null
    },
    INBOUND: inboundData,
    OUTBOUND: {
      ipv4_unicast_cpr: outboundEntries,
      rawOutboundData: rawOutboundData
    }
  };

  return cpr;
}

/**
 * Extracts CPR data for a specific reflector from the cached cluster API response
 * @param clusterResponseData - The full array response from cluster API (CTX-25 contract):
 * [{ id: subId, name, status, lastUpdate, data | null }, ...]
 * @param selectedReflectorName - The selected reflector subId to extract data for
 * @returns CPR data for the selected reflector, or null if not found / has no document yet.
 * Matches strictly by `id` (subId) — never `name` — and never falls back to another member's
 * document, which would show stale/wrong data as if it belonged to the selected reflector.
 * Use `getEntityStatusInfo` alongside this to distinguish "down/no data" from "not found".
 */
export function extractCprDataForClusterReflector(
  clusterResponseData: VisibilityMemberResponse[],
  selection: string | ClusterMemberSelection | null
): CPR | null {
  if (!clusterResponseData || !Array.isArray(clusterResponseData) || clusterResponseData.length === 0) {
    return null;
  }

  // Helper to extract apiData from response item
  const extractApiData = (item: VisibilityMemberResponse) => {
    if (!item || !item.data) {
      return null;
    }
    // Data contains neighbors/vpcs directly, wrap in INBOUND/OUTBOUND
    if (item.data.neighbors || item.data.vpcs) {
      return {
        INBOUND: item.data,
        OUTBOUND: item.data.OUTBOUND || null
      };
    } else if (item.data.INBOUND || item.data.OUTBOUND) {
      // Alternative: data already has INBOUND/OUTBOUND structure
      return item.data;
    }
    return null;
  };

  const responseItem = selectionKeys(selection).length > 0
    ? clusterResponseData.find(item => memberMatchesSelection(item, selection))
    : clusterResponseData[0];

  if (!responseItem) {
    return null;
  }

  const apiData = extractApiData(responseItem);
  if (!apiData) {
    return null;
  }
  return transformApiResponseToCPR(apiData, responseItem.id, 'reflector');
}

export interface ClusterMemberCpr {
  reflectorId: string;
  reflectorName: string;
  role: 'PRIMARY' | 'SECONDARY' | null;
  status: 'online' | 'offline' | 'unknown';
  cpr: CPR | null;
}

/**
 * Extracts every cluster member's CPR (not just the selected one) from a cached cluster
 * response, preserving each member's reflector identity. Feeds the "all cluster members"
 * scope of Table mode. Members with `data: null` (down / no document yet) are included with
 * `cpr: null` so callers can still surface their identity/status.
 */
export function extractAllClusterMemberCprs(
  clusterResponseData: VisibilityMemberResponse[]
): ClusterMemberCpr[] {
  if (!clusterResponseData || !Array.isArray(clusterResponseData)) {
    return [];
  }

  return clusterResponseData.map(member => ({
    reflectorId: member.id,
    // Older backends omit `name` and put the display name in `id` — fall back so the
    // "Reflector" column / selector never render blank.
    reflectorName: member.name || member.id,
    role: normalizeClusterRole(member.role),
    status: member.status,
    cpr: extractCprDataForClusterReflector([member], member.id)
  }));
}

/** A member is "up" when it reports online and actually carries a document (data → cpr). */
export function isClusterMemberUp(member: ClusterMemberCpr): boolean {
  return member.status !== 'offline' && member.cpr !== null;
}

/**
 * Chooses which cluster member should be shown by default in the topology, given the sidebar
 * reflector list and the `/visibility` members (which carry HA `role` + live status). Preference:
 *   1. an up PRIMARY,
 *   2. an up SECONDARY — the PRIMARY-down fallback (CTX-71),
 *   3. any up member,
 *   4. the first reflector — deterministic, non-crashing when every member is down or `role`
 *      is null for both.
 * Returns the SIDEBAR reflector id to select (matched to its member across the subId/name
 * id-spaces via `memberMatchesSelection`), or null when there are no reflectors.
 */
export function pickPreferredClusterMember(
  reflectors: Array<{ id: string; display_name?: string | null }>,
  members: ClusterMemberCpr[] | null | undefined
): string | null {
  if (!reflectors || reflectors.length === 0) {
    return null;
  }

  const memberFor = (reflector: { id: string; display_name?: string | null }): ClusterMemberCpr | undefined =>
    members?.find(m =>
      memberMatchesSelection(
        { id: m.reflectorId, name: m.reflectorName },
        { id: reflector.id, name: reflector.display_name ?? null }
      )
    );

  const firstUpWithRole = (role: 'PRIMARY' | 'SECONDARY') =>
    reflectors.find(r => {
      const m = memberFor(r);
      return m ? m.role === role && isClusterMemberUp(m) : false;
    });

  const primaryUp = firstUpWithRole('PRIMARY');
  if (primaryUp) {
    return primaryUp.id;
  }

  const secondaryUp = firstUpWithRole('SECONDARY');
  if (secondaryUp) {
    return secondaryUp.id;
  }

  const anyUp = reflectors.find(r => {
    const m = memberFor(r);
    return m ? isClusterMemberUp(m) : false;
  });
  if (anyUp) {
    return anyUp.id;
  }

  return reflectors[0].id;
}



/**
 * Rows per round trip when reading `/visibility/routes`.
 *
 * The endpoint clamps `page_size` to 500, so asking for more is silently reduced — this is the
 * largest page it will actually serve, chosen to keep the number of round trips small rather
 * than to avoid paging. Paging is still mandatory: a cluster of busy members exceeds 500 rows
 * easily, and the cap means no single request can ever be trusted to be the whole set.
 */
export const VISIBILITY_ROUTES_PAGE_SIZE = 500;

/**
 * Ceiling on round trips for one read, so a backend that keeps reporting a `total` it never
 * satisfies costs a bounded number of requests instead of spinning. Reaching it is reported as
 * an incomplete read, never as success.
 */
const MAX_VISIBILITY_ROUTE_PAGES = 40;

export interface VisibilityRouteRowsResult {
  rows: RouteRow[];
  /** `pagination.total` as the endpoint last reported it. */
  total: number;
  /**
   * Whether `rows` covers `total`. A short read must stay visible to callers: the surfaces
   * narrow rows per cluster member, so a member whose rows never arrived is indistinguishable
   * from one announcing nothing — which is exactly how CTX-687 reached production.
   */
  complete: boolean;
}

/**
 * Reads every page of `GET /visibility/routes` for one entity.
 *
 * `/visibility/routes` is paginated, so one page is a sample of the entity and never the whole
 * of it. For a cluster that sample used to be worse than a sample: the endpoint concatenated its
 * members one after another, so reading only the first page returned *all* of the first member
 * and *none* of the last, and the surfaces rendered a healthy reflector as announcing nothing
 * (CTX-687). CTX-729 interleaves the members instead, so a short read now shows every member with
 * fewer rows each — it looks short instead of looking like a silent reflector, but it is still
 * short. Every page is read here, once, so nothing downstream has to know the endpoint paginates,
 * and nothing depends on the order it serves them in.
 *
 * Returns `null` when the request fails, which is the caller's signal to fall back to the
 * `/visibility` document — distinct from an empty but complete result.
 */
export async function fetchAllVisibilityRouteRows(
  token: string,
  entityType: 'reflector' | 'cluster',
  entityId: string,
  direction: 'inbound' | 'outbound',
  tenantId: number,
  serverFilters: Record<string, string>,
  signal?: AbortSignal
): Promise<VisibilityRouteRowsResult | null> {
  const rows: RouteRow[] = [];
  // `null` while the endpoint has not reported one — "unknown", not "zero". Conflating the two
  // would make the very first page look like it covered the set.
  let total: number | null = null;

  for (let page = 1; page <= MAX_VISIBILITY_ROUTE_PAGES; page++) {
    const response = await getBgpVisibilityRoutes(
      token,
      entityType,
      entityId,
      direction,
      tenantId,
      { ...serverFilters, page: String(page), page_size: String(VISIBILITY_ROUTES_PAGE_SIZE) },
      signal
    );

    // The transport answers null in fixture builds; treat it as "no server rows to use" rather
    // than as an empty set, so the caller keeps the document.
    if (!response) {
      return null;
    }

    const reportedTotal = Number(response.pagination?.total);
    if (Number.isFinite(reportedTotal)) {
      total = reportedTotal;
    }

    // Trust the page the endpoint says it served, not the one we asked for. A backend that
    // ignores `page` would otherwise hand back the first one until the accumulated count passed
    // `total` — satisfying the completeness check with duplicates while still missing every
    // member past the page boundary, i.e. the exact defect this function exists to fix.
    const servedPage = Number(response.pagination?.page);
    if (Number.isFinite(servedPage) && servedPage !== page) {
      return { rows, total: total ?? rows.length, complete: false };
    }

    const items = Array.isArray(response.items) ? response.items : [];
    rows.push(...mapWireRouteRows(items as WireRouteRow[]));

    // A page the endpoint could not fill is the end of the set, whether or not it reported a
    // total. Measured against the size it says it served, not the size we asked for, since it
    // clamps `page_size` to its own ceiling.
    const servedSize = Number(response.pagination?.page_size) || VISIBILITY_ROUTES_PAGE_SIZE;
    if (items.length < servedSize) {
      break;
    }
    if (total !== null && rows.length >= total) {
      break;
    }
  }

  // No reported total and no short page means the loop hit its ceiling: everything read is real,
  // but there is no evidence it is everything. That is an incomplete read, not a full one.
  return {
    rows,
    total: total ?? rows.length,
    complete: total === null ? rows.length < MAX_VISIBILITY_ROUTE_PAGES * VISIBILITY_ROUTES_PAGE_SIZE : rows.length >= total
  };
}
